/**
 * @module TeamDashboardTeamSelector
 */
/**
 * @Author: Anandp
 * @Date:   2024-12-10 15:13:21
 * @Last Modified by:   Eswari
 * @Last Modified time: 2026-01-28
 */
import * as React from "react";
import {
  Box,
  Drawer,
  List,
  Checkbox,
  Typography,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  CircularProgress,
  Backdrop,
  OutlinedInput,
  InputAdornment,
  SvgIcon,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { Scrollbar } from "src/components/scrollbar";
import { createTypography } from "src/theme/base/customTypography";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useUser } from "src/hooks/use-user";
import { useTeam } from "src/contexts/team-context";
import { useSidebar } from 'src/contexts/SidebarContext'; // Import the context hook
import teamDashboardResultsApi from "src/services/teamdashboardApi";
import { useState, useEffect } from "react";

const SIDE_NAV_WIDTH = 210;
const SIDE_NAV_MINIMIZED_WIDTH = 50; 

export default function TemporaryDrawer(props) {
  const setDrawerOpen = props.setDrawerOpen;
  const navigate = useNavigate();
  const { isMinimized } = useSidebar();
  const typography = createTypography();
  const user = useUser();
  const accessToken = sessionStorage.getItem("accessToken");
  const {
    selectedTeamId,
    setTeamId,
    setTeamName,
    setToClickWithOrg,
    clickWithOrg,
    clickWithPage,
    seteamChange,
    setIsLoadingTeam,
  } = useTeam();

  const [teamsList, setTeamsList] = useState([]);
  const [searchData, setSearchData] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [noTeam, setNoTeam] = useState(false);

  /* ------------------ Fetch Teams (ONCE) ------------------ */
  /* Fetches the user's teams when the component mounts and updates the teams list state. 
  * @function fetchUsersTeams
  * @returns {void}
  * @description
  * This function sends a request to fetch the user's teams using the provided `accessToken` and `user.id`.
  * It handles various scenarios:
  * - If the API returns a message, it displays an error toast with that message.
  * - If no teams are found for the user, it sets the `noTeam` state to `true`.
  * - If teams are found, it updates the `teamsList` state with the user's teams.
  * The function also manages the loading state (`isLoading`) appropriately.
  * @example
  * // Fetch the user's teams and update the relevant states
  * fetchUsersTeams();
  * -----------------------------------------------------------
  */
  useEffect(() => {
    const fetchUsersTeams = async () => {
      try {
        const response = await teamDashboardResultsApi.getUsersTeams(
          accessToken,
          user.id
        );

        if (
          response?.message ||
          !response?.teamDetails?.userDetails?.length
        ) {
          setNoTeam(true);
        } else {
          setTeamsList(response.teamDetails.userDetails);
        }
      } catch (error) {
        toast.error("Failed to load teams");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsersTeams();
  }, [accessToken, user.id]);

  /* ------------------ Restore Team from Session ------------------ 
  * Restores the selected team from session storage when the teams list is updated.
  *
  * @function restoreTeamFromSession
  * @returns {void} 
  * @description
  * This effect runs whenever the `teamsList` state changes. It checks if there are any teams in the list.
  * If teams are available, it retrieves the stored team ID and name from session storage.
  * If both values are found, it updates the `selectedTeamId` and `teamName` states using the provided setter functions.
  * This ensures that the user's previously selected team is restored when the component re-renders.
  * @example
  * // Restore the selected team from session storage
  * restoreTeamFromSession();
  * -----------------------------------------------------------
  */
  useEffect(() => {
    if (!teamsList.length) return;

    const storedTeamId = sessionStorage.getItem("teamDashboardTeamId");
    const storedTeamName = sessionStorage.getItem("teamDashboardTeamName");

    if (storedTeamId && storedTeamName) {
      setTeamId(Number(storedTeamId));
      setTeamName(storedTeamName);
    }
  }, [teamsList, setTeamId, setTeamName]);
  
  /* ------------------ Memoized Search ------------------ 
  * Memoizes the filtered teams list based on the search data.
  * @function filteredTeamsList
  * @returns {Array} - The filtered list of teams.
  */
  const filteredTeamsList = React.useMemo(() => {
    if (!searchData) return teamsList;
    return teamsList.filter((team) =>
      team.teamName.toLowerCase().includes(searchData.toLowerCase())
    );
  }, [searchData, teamsList]);

  /* ------------------ Team Selection ------------------ 
  * Handles the change event for selecting a team from the drawer and updates the state accordingly.
  *
  * @function handleTeamChange
  * @param {Object} event - The event object triggered by the change action.
  * @param {number} teamId - The ID of the selected team.
  * @param {string} teamName - The name of the selected team.
  * @returns {void}
  *
  * @description
  * This function is responsible for handling the change event when a team is selected from the drawer.
  * It updates the following states:
  * - `selectedTeamId` with the provided `teamId`.
  * - `teamName` with the provided `teamName`.
  * - It also updates sessionStorage with the selected team’s ID and name for persistence across sessions.
  * - Additionally, the function resets certain state values like `toClickWithOrg` and triggers navigation to the team diversity page.  
  * @example
  * // Handle team selection change
  * handleTeamChange(event, 1, "Team A");
  */
  const handleTeamChange = (_, teamId, teamName) => {
    setIsLoadingTeam(true);
    setToClickWithOrg(false);
    setTeamId(teamId);
    setTeamName(teamName);
    setState("left", false);

    sessionStorage.setItem("teamDashboardTeamId", teamId);
    sessionStorage.setItem("teamDashboardTeamName", teamName);

    seteamChange(true);
    navigate("/teamDiversity");
  };

  const [state, setState] = React.useState({
    left: false,
  });

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    // Check if the click event originated from within a checkbox
    if (event.target.tagName === "INPUT" && event.target.type === "checkbox") {
      return;
    }

    setState({ ...state, [anchor]: open });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const list = (anchor) => (
    <Box
      sx={{
        width: anchor === "top" || anchor === "bottom" ? "auto" : {lg:'310px', md:'310px', sm:'280px', xs:'280px'},
        height: "100%",
        backgroundColor: "#583F99",
        borderRadius: "0 15px 15px 0",
        zIndex: 1201,
        paddingLeft: {lg:'45px',md:'45px',sm:'5px',xs:'5px'},
        paddingTop: "25px",
       
      }}
      role="presentation"
      onClick={(event) => {
        if (event.target !== event.currentTarget) return; // Don't close on input events
        toggleDrawer(anchor, false);
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return; // Don't close on input events
        toggleDrawer(anchor, false);
      }}
      p={2}
    >
      <Typography
        sx={{ ...typography.subtitle.regularColorless, color: "white" }}
      >
        Team Selector
      </Typography>
      <OutlinedInput
          value={searchData}
          autoFocus
          fullWidth
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiInputBase-input': { padding: '8px 0!important' },
            '&.MuiInputBase-root': { backgroundColor: '#fff!important' },
            '& .MuiInputAdornment-root path': { fill: '#535B66' },
            '& .MuiSvgIcon-root': { width: '16px', height: '16px' },
            ' input::placeholder': { color: '#535B66', fontSize: '12px' },
            

          }}
          placeholder="Search"
          onChange={(e) => setSearchData(e.target.value)}
          startAdornment={
            <InputAdornment position="start"
            >
              <SvgIcon>
                <SearchIcon fontSize="small" />
              </SvgIcon>
            </InputAdornment>
          }
        />
      <Scrollbar style={{ maxHeight: "100%", width: "100%" }}>
      {!clickWithOrg && !selectedTeamId && noTeam && (
          <List>
            <div>
              <Accordion sx={{ backgroundColor: "transparent" }}>
                <AccordionSummary>
                  <Typography
                    sx={{
                      ...typography.subtitle.regularColorless,
                      color: "white",
                    }}
                  >
                    No team to list down
                  </Typography>
                </AccordionSummary>
              </Accordion>
            </div>
          </List>
        )}
        {clickWithOrg && clickWithPage && Array.isArray(filteredTeamsList) && (
          <List>
          {filteredTeamsList.map((team, index) => (
            <div key={index}>
              <Accordion
               sx={{ backgroundColor: "transparent",
                
                }}>
                <AccordionSummary
                
                  // expandIcon={<PlusBUtton />}
                  aria-controls={`panel${index + 1}-content`}
                  id={`panel${index + 1}-header`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <FormControlLabel

                    label={
                      <Typography
                        sx={{
                          ...typography.subtitle.regularColorless,
                          color: "white",
                        }}
                      >
                        {team.teamName}
                      </Typography>
                    }
                    control={
                      <Checkbox
                        checked={selectedTeamId === team.teamId}
                        onChange={(event) =>
                          handleTeamChange(event, team.teamId, team.teamName)
                        }
                      />
                    }
                  />
                </AccordionSummary>
                {/* <AccordionDetails>{children}</AccordionDetails> */}
              </Accordion>
            </div>
          ))}
          </List>
        )}
        {/* {!clickWithOrg && selectedTeamId && Array.isArray(filteredTeamsList) && ( */}
        {!clickWithOrg && Array.isArray(filteredTeamsList) && (
        <List>
          {filteredTeamsList.map((team, index) => (
            <div key={index}>
              <Accordion sx={{ backgroundColor: "transparent" }}>
                <AccordionSummary
                  // expandIcon={<PlusBUtton />}
                  aria-controls={`panel${index + 1}-content`}
                  id={`panel${index + 1}-header`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <FormControlLabel
                    label={
                      <Typography
                        sx={{
                          ...typography.subtitle.regularColorless,
                          color: "white",
                        }}
                      >
                        {team.teamName}
                      </Typography>
                    }
                    control={
                      <Checkbox
                        checked={selectedTeamId === team.teamId}
                        onChange={(event) =>
                          handleTeamChange(event, team.teamId, team.teamName)
                        }
                      />
                    }
                  />
                </AccordionSummary>
                {/* <AccordionDetails>{children}</AccordionDetails> */}
              </Accordion>
            </div>
          ))}
        </List>
        )}
      </Scrollbar>
    </Box>
  );

  React.useEffect(() => {
    // Open the drawer directly when the component mounts
    setState((prev) => ({ ...prev, left: true }));
  }, []);

  useEffect(() => {
    if (teamsList) {
      setIsLoading(false);
    }
  }, [teamsList]);

  //loader animation will run until response fetched from backend
  if (isLoading) {
    return (
      <div>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    );
  }

  // if(noTeam) {
  //    return (
  //     <div
  //       style={{
  //         display: "flex",
  //         justifyContent: "center",
  //         alignItems: "center",
  //         height: "100vh", // Adjust this based on your layout
  //       }}
  //     >
  //       <SldButtons.BlackButtonCap variant="contained">
  //         <Typography textAlign="center">
  //           User <br />
  //           Not Yet Assigned to Team
  //         </Typography>
  //       </SldButtons.BlackButtonCap>
  //     </div>
  //   )
  // }

  return (
    <Drawer
      anchor="left"
      open={state["left"]}
      onClose={() => {
        setDrawerOpen(false);
        toggleDrawer("left", false);
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0)!important", // Set the background color of the backdrop to transparent
        },
       '& .MuiPaper-root.MuiPaper-elevation': {
          left: {
            lg: isMinimized ? SIDE_NAV_MINIMIZED_WIDTH : SIDE_NAV_WIDTH,
            md: isMinimized ? SIDE_NAV_MINIMIZED_WIDTH : SIDE_NAV_WIDTH,
            sm: 0,
            xs: 0,
          },
          top:{lg:'0',md:'0', sm:'155px', xs:'155px'},
          zIndex: -1, overflow: 'hidden',
          height:{lg:'100%', md:'100%', sm:'70%', xs:'calc(100vh - 200px)'}
        },

        '& .MuiPaper-root.MuiPaper-elevation .MuiPaper-root.MuiPaper-elevation': { left: 0, zIndex: 1,top:{lg:' ',md:'', sm:'0', xs:'0'},
        height:'100%' },
        "& .MuiButtonBase-root": {
          "& rect": { fill: "#fff", stroke: "#fff;" },
        },
        "& .MuiAccordionDetails-root .MuiBox-root": { marginLeft: 0 },
        "& .MuiAccordionSummary-root": {
          borderBottom: "1px solid rgba(255,255,255,.3)",
          borderTop: "1px solid rgba(255,255,255,.3)",
        },
        "& .MuiAccordionSummary-content": { margin: "2px 0!important" },
        "& .MuiAccordionSummary-root.Mui-expanded": {
          minHeight: "25px",
          margin: "2px 0",
        },
        "& .MuiButtonBase-root.MuiCheckbox-root": { padding: "4px" },
        "& .MuiFormControlLabel-root .MuiTypography-root": { paddingLeft: 0 },
        '& .MuiButtonBase-root.Mui-checked':{color:'#fff!important'}

      }}
    >
      {list("left")}

    </Drawer>
  );
}
